import type { DailyRoutineDetails } from '../dto/DailyRoutineDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../ports/EmotionalProtocolLogRepository';
import type { IdGenerator } from '../ports/IdGenerator';
import type { EmotionalState } from '../../domain/emotion/enums';
import { toEmotionalStateData } from '../../domain/emotion/EmotionalStateContent';
import type { EmotionalProtocolLogId, ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';
import { loadRoutineDetails } from './routineDetails';

/**
 * Declares (or clears, with `state: null`) the user's emotional state for a logical date.
 * Independent of the day mode: the mode keeps driving mission count, priority and exigence;
 * the state only ever influences presentation (accent, image, tone, thought of the day,
 * intention, routines) through resolvePresentation() on the presentation layer.
 */
export class SetEmotionalState {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly emotionalProtocolLogs: EmotionalProtocolLogRepository,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
    state: EmotionalState | null,
  ): Promise<Result<DailyRoutineDetails, RoutineError>> {
    try {
      const routine = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (!routine) return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      if (routine.profileId !== profileId) {
        return failure(
          new RoutineError('emotional_state_forbidden', 'Cette journée ne peut pas être modifiée.'),
        );
      }

      const timestamp = this.clock.now().toISOString();
      const existing = await this.emotionalProtocolLogs.findByLogicalDate(profileId, logicalDate);
      await this.emotionalProtocolLogs.save({
        id: existing?.id ?? this.ids.generate<EmotionalProtocolLogId>(),
        profileId,
        logicalDate,
        data: toEmotionalStateData({ state }),
        createdAt: existing?.createdAt ?? timestamp,
        updatedAt: timestamp,
      });

      return success(await loadRoutineDetails(routine, this.dailySteps, this.emotionalProtocolLogs));
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
